import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BrainIcon, BuildingIcon, PencilIcon, XIcon } from 'lucide-react';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { whenLabel, type MemoryIndexEntry } from '@/pages/assistant/shared';

/**
 * The assistant's memory list — each row links to the memory's own view page
 * (/assistant/memory/:id); editing lives on a separate edit route. The
 * Organization tab is a deliberate placeholder until the dreaming promotion
 * flow (Phase 4) exists.
 */

const TABS = { personal: 'personal', organization: 'organization' } as const;
type Tab = (typeof TABS)[keyof typeof TABS];

/** The memory fields the search box matches. */
const MEMORY_SEARCH_FIELDS: ReadonlyArray<keyof MemoryIndexEntry> = ['description', 'path'];

export function MemoryPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>(TABS.personal);
  const [deleting, setDeleting] = useState<MemoryIndexEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { search, setSearch } = useUrlSearch();

  const memories = useQuery({
    queryKey: ['assistant-memories'],
    queryFn: ({ signal }) => getJson<MemoryIndexEntry[]>('/assistant/memories', undefined, signal),
  });
  const rows = filterRows(memories.data ?? [], search, MEMORY_SEARCH_FIELDS);

  const remove = useMutation({
    mutationFn: (path: string) =>
      sendJson('delete', `/assistant/memories/file?path=${encodeURIComponent(path)}`),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-memories'] });
    },
    onError: (error) => {
      setDeleting(null);
      setNotice(errorMessage(error));
    },
  });

  return (
    <TooltipProvider delayDuration={100}>
      <section className="animate-portal-rise">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title="Memory"
          subtitle="What the assistant remembers about working with you — yours to edit or forget."
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SegmentedControl
            label="Which memory to show"
            options={[
              { key: TABS.personal, label: 'Personal' },
              { key: TABS.organization, label: 'Organization' },
            ]}
            value={tab}
            onChange={(next) => setTab(next as Tab)}
          />
          {tab === TABS.personal ? (
            <SearchInput
              label="Search memories"
              placeholder="Description or path"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          ) : null}
          {tab === TABS.personal && memories.data !== undefined ? (
            <span className="text-portal-micro text-portal-muted">
              {memories.data.length} {memories.data.length === 1 ? 'memory' : 'memories'}
            </span>
          ) : null}
        </div>

        {notice === null ? null : (
          <div className="mb-3">
            <ErrorNotice inline title="Something went wrong" message={notice} />
          </div>
        )}

        {tab === TABS.organization ? (
          <Panel>
            <EmptyState
              icon={<BuildingIcon aria-hidden className="size-6" />}
              title="Organization memory is coming"
              message="Org-wide memory will be curated by the assistant's nightly review and approved by admins before anything is shared — it arrives with that promotion flow."
            />
          </Panel>
        ) : memories.isError ? (
          <ErrorNotice title="Memory unavailable" message={errorMessage(memories.error)} />
        ) : memories.isPending ? (
          <SkeletonTable label="Loading memories…" />
        ) : memories.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<BrainIcon aria-hidden className="size-6" />}
              title="Nothing remembered yet"
              message="As you chat, the assistant saves durable preferences and facts here — and you can edit or remove anything."
            />
          </Panel>
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState title="No memories match" message="Try a different search." />
          </Panel>
        ) : (
          <DataTable headers={['Memory', 'Updated', 'Version', '']} minWidth={640}>
            {rows.map((entry) => (
              <tr key={entry.id} className="border-t border-portal-line align-top">
                <td className="p-3">
                  <Link
                    to={`/assistant/memory/${entry.id}`}
                    className="text-portal-small font-bold text-brand hover:underline"
                  >
                    {entry.description || entry.path}
                  </Link>
                  <span className="block font-mono text-portal-micro text-portal-muted">
                    {entry.path}
                  </span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">
                  {whenLabel(entry.updatedAt) ?? '—'}
                </td>
                <td className="p-3 text-portal-small">v{entry.version}</td>
                <td className="p-3">
                  <span className="flex items-center justify-end gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to={`/assistant/memory/${entry.id}/edit`}
                          aria-label="Edit memory"
                          className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                        >
                          <PencilIcon aria-hidden className="size-3.5" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Edit</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Forget memory"
                          onClick={() => setDeleting(entry)}
                          className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                        >
                          <XIcon aria-hidden className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Forget</TooltipContent>
                    </Tooltip>
                  </span>
                </td>
              </tr>
            ))}
          </DataTable>
        )}

        <ConfirmDialog
          open={deleting !== null}
          tone="danger"
          title="Forget this memory?"
          message={
            deleting ? `"${deleting.description || deleting.path}" will be permanently removed.` : ''
          }
          confirmLabel="Forget it"
          onConfirm={() => {
            if (deleting) remove.mutate(deleting.path);
          }}
          onCancel={() => {
            if (!remove.isPending) setDeleting(null);
          }}
          pending={remove.isPending}
        />
      </section>
    </TooltipProvider>
  );
}
